<template>
  <form
    class="new-invoice"
    @submit.prevent="onSubmit"
  >
    <div class="container">
      <div
        class="modal"
        :class="{ 'is-active': modal }"
      >
        <div class="modal-background" />
        <div class="modal-content">
          <Component
            :is="component"
            :limit="creditLimitNeeded"
            :debt="customerDebt"
            @close="onCloseModal()"
          />
        </div>
        <button
          class="modal-close is-large"
          aria-label="close"
          @click="onCloseModal"
        />
      </div>
      <div class="columns is-multiline">
        <div class="column is-6">
          <Receiver :is-loading="loading" />
        </div>

        <div class="column is-6">
          <VoucherDetails
            ref="voucherDetailsComponent"
            :is-loading="loading"
          />
        </div>
      </div>
    </div>

    <div class="container">
      <Concepts :is-loading="loading" />
      <CfdisRelatedReplacement replacement-type="replacementInvoice" />
      <Comments />

      <div class="field is-grouped">
        <p class="control">
          <button
            class="button is-primary"
            :class="{ 'is-loading': loading }"
            :disabled="loading"
            @click="setStamp(false)"
          >
            Guardar
          </button>
        </p>
        <p class="control">
          <button
            class="button is-success"
            :class="{ 'is-loading': loading }"
            type="submit"
            :disabled="loading"
            @click="setStamp(true)"
          >
            Guardar y Timbrar
          </button>
        </p>
        <p class="control">
          <RouterLink
            class="button is-danger"
            :class="{ 'is-loading': loading }"
            :disabled="loading"
            :to="{ name: 'Home' }"
          >
            Cancelar
          </RouterLink>
        </p>
      </div>
      <RouterView />
    </div>
  </form>
</template>

<script>
import get from 'lodash/get';
import merge from 'lodash/merge';
import { mapMutations, mapActions, mapGetters } from 'vuex';

export default {
  name: 'NewInvoice',
  components: {
    Receiver: () => import(/* webpackChunkName: "invoice-receiver" */ '@/components/Admin/Invoices/NewInvoice/Receiver.vue'),
    VoucherDetails: () => import(/* webpackChunkName: "invoice-voucher-details" */ '@/components/Admin/Invoices/NewInvoice/VoucherDetails.vue'),
    Concepts: () => import(/* webpackChunkName: "invoice-concepts" */ '@/components/Admin/Invoices/NewInvoice/Concepts.vue'),
    CfdisRelatedReplacement: () => import(/* webpackChunkName: "cfdis-related-replacement" */ '@/components/Admin/CFDI/CfdisRelatedReplacement.vue'),
    Comments: () => import(/* webpackChunkName: "cfdis-comments" */ '@/components/Admin/CFDI/Comments.vue'),
    LimitCreditModal: () => import(/* webpackChunkName: "limit-credit-modal" */ '@/components/Admin/Invoices/NewInvoice/LimitCreditModal.vue'),
  },
  data() {
    return {
      loading: false,
      stamp: false,
      isSaleNote: false,
      modal: false,
      component: '',
      creditLimitNeeded: 0,
      customerDebt: 0,
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from.name !== 'CustomerEdit') {
        vm.setInitialState();
        vm.setInitialStateCustomer();

        vm.$store.commit(
          'users/setValues',
          { email: '', name: '' },
        );
      }
      next();
    });
  },
  beforeRouteUpdate(to, { name }, next) {
    if (name === 'NewInvoiceCustomerSearcher') {
      this.$refs.voucherDetailsComponent.focusEmail();
    }
    next();
  },
  computed: merge(
    mapGetters({
      totalCurrency: 'invoice/totalCurrency',
    }),
  ),
  methods: merge(
    mapMutations({
      setInitialState: 'invoice/setInitialState',
      setInitialStateCustomer: 'customers/setInitialState',
    }),
    mapActions({
      saveInvoice: 'invoice/save',
    }),
    {
      async openModal(component) {
        this.component = component;
        this.modal = true;
      },
      onCloseModal() {
        this.modal = false;
        this.component = '';
      },
      setStamp(value) {
        this.stamp = value;
      },
      setSaleNote(value) {
        this.isSaleNote = value;
      },
      async checkLimit(customer, invoice) {
        if (this.$store.state.customers.id && invoice.voucherPaymentMethod === 'PPD') {
          await this.$store.dispatch('customers/getSingle', this.$store.state.customers.id);

          const debtCurrency = (this.$store.state.customers.debtCreditLimit[0])
            ? this.$store.state.customers.debtCreditLimit[0].totalAmount
            : 0;

          if (debtCurrency) {
            const totalCurrency = Number(this.totalCurrency.replace(/[^0-9.]/g, ''));
            const voucherTotal = (invoice.voucherCurrency !== 'MXN')
              ? (totalCurrency * invoice.voucherExchangeRate)
              : totalCurrency;

            this.creditLimitNeeded = (debtCurrency + voucherTotal).toFixed(2);
            this.customerDebt = debtCurrency.toFixed(2);

            return customer.creditLimit >= this.creditLimitNeeded
            || customer.creditLimit === 0;
          }
        }
        return true;
      },

      async onSubmit() {
        const { stamp, isSaleNote } = this;
        const { haveEfirma, id: companyId } = this.$store.state.company;

        const validateLimitCredit = await this.checkLimit(
          this.$store.state.customers,
          this.$store.state.invoice,
        );

        if (validateLimitCredit) {
          this.loading = true;
          try {
            const payload = await this.saveInvoice({ stamp, isSaleNote });

            let content = '';
            let name = '';

            if (isSaleNote) {
              content = 'Nota de Remisión generada correctamente.';
              name = 'SaleNoteList';
            } else {
              content = stamp ? 'Timbrado exitoso' : 'Guardado correctamente';
              name = stamp ? 'InvoicesHistory' : 'PreInvoiceList';
            }

            this.$store.commit('ui/setValue', {
              accessor: 'catalogs.sku',
              value: [],
            });

            this.loading = false;

            const to = { name };

            if (!haveEfirma && stamp) {
              this.$warning({ message: 'Necesitas cargar tu CSD para emitir la factura' });

              to.name = 'Company';
              to.params = { id: companyId };
              to.query = { stamp: get(payload, '_doc._id', '') };
            } else {
              this.$success({ message: content });
            }

            this.$router.push(to);
          } catch (error) {
            this.$danger({
              message: stamp
                ? error.message
                : 'Ocurrio un error al guardar.',
            });

            this.loading = false;
          }
        } else {
          this.$warning({ message: 'El cliente ha alcanzado su límite de crédito' });
          this.openModal('LimitCreditModal');
        }
      },
    },
  ),
};
</script>

<style scoped>
.action-buttons {
  display: flex;
  justify-content: flex-end;
}

.field.is-grouped {
  justify-content: flex-end;
  padding-bottom: 2rem;
}

.columns {
  margin: 0;
}

.container {
  padding: 0 .75rem;
}

</style>
